import { Delete } from '@mui/icons-material';
import { Alert, Grid } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';

import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAPIKeysMutation, useAPIKeysQuery } from 'api/hooks/apiKeys';
import { ConfirmationDialog, CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { AlertActions } from '../../Dashboard/Alert/slice';
import { APIKeyCreationDialog } from './APIKeyCreationDialog';
import { APIKeyShowDialog } from './APIKeyShowDialog';

export const APIKeysPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apiKeyToShow, setAPIKeyToShow] = useState<string | null>(null);
  const [keyIdToDelete, setKeyIdToDelete] = useState<string | null>(null);
  const { creation, deletion } = useAPIKeysMutation();
  const { data, isLoading } = useAPIKeysQuery();

  usePageHeading(t(translations.pages.chat.apiKeyManagement()));

  const handleKeyCreate = async ({ name, description }: { name: string; description: string }) => {
    try {
      const { apiKey } = await creation.mutateAsync({ name, description });
      setKeyDialogOpen(false);
      setAPIKeyToShow(apiKey);
      dispatch(AlertActions.success(t(translations.pages.chat.apiKeys.alerts.createSuccess())));
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.chat.apiKeys.alerts.failedCreate())));
    }
  };

  const handleKeyDelete = async () => {
    try {
      if (!keyIdToDelete) return;
      await deletion.mutateAsync(keyIdToDelete);
      dispatch(AlertActions.success(t(translations.pages.chat.apiKeys.alerts.deleteSuccess())));
      setDeleteDialogOpen(false);
      setKeyIdToDelete(null);
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.chat.apiKeys.alerts.failedDelete())));
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams<any>) => {
      return [
        <GridActionsCellItem
          icon={<Delete color="error" />}
          label={t(translations.common.delete())}
          onClick={() => {
            setKeyIdToDelete(params.id.toString());
            setDeleteDialogOpen(true);
          }}
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<{
    name: string;
    description: string;
    createdAt: Date;
    expires: Date;
  }> => {
    return [
      {
        field: 'name',
        headerName: t(translations.common.name()),
        flex: 2,
        renderCell: params => params.row.name || '-'
      },
      {
        field: 'description',
        headerName: t(translations.common.description()),
        flex: 5,
        sortable: false,
        renderCell: params => params.row.description || '-'
      },
      {
        field: 'expires',
        headerName: t(translations.common.expires()),
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => params.value && format(new Date(params.value), 'dd.MM.yyyy')
      },
      {
        field: 'createdAt',
        headerName: t(translations.common.createdAt()),
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => format(new Date(params.value), 'dd.MM.yyyy HH:mm')
      },
      {
        field: 'actions',
        headerName: t(translations.common.actions()),
        flex: 0.5,
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <Grid container gap={1}>
      <APIKeyCreationDialog
        onClose={() => setKeyDialogOpen(false)}
        onCreate={handleKeyCreate}
        open={keyDialogOpen}
      />
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t(translations.pages.chat.apiKeys.deleteDialog.title())}
        onConfirm={handleKeyDelete}
      >
        {t(translations.pages.chat.apiKeys.deleteDialog.description())}
      </ConfirmationDialog>
      <APIKeyShowDialog apiKeyToShow={apiKeyToShow} handleClose={() => setAPIKeyToShow(null)} />
      <Grid container justifyContent={'space-between'} alignItems={'center'}>
        <Alert severity="info">
          <div
            dangerouslySetInnerHTML={{ __html: t(translations.pages.chat.apiKeys.alerts.info()) }}
          />
        </Alert>
        <CreateButton onClick={() => setKeyDialogOpen(true)} />
      </Grid>
      <DataGrid
        columns={columns}
        rows={data?.data || []}
        autoHeight
        sx={{ minHeight: '70vh' }}
        loading={isLoading}
        localeText={{ noRowsLabel: t(translations.pages.chat.apiKeys.noRows()) }}
      />
    </Grid>
  );
};
